import {debounce} from "lodash";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {collectImageUrl, IMAGES_INITIAL, photoUrlsInitial} from "./helpers";

const devicePixelRatio = window.devicePixelRatio;

export const useResponsiveImage = (id, boxRef, scaleFactor, refreshKey) => {
  const [photoUrls, setPhotoUrls] = useState(photoUrlsInitial);

  const didInit = useRef(false);

  const onSetPhotoUrls = useCallback(() => {
    const width = boxRef.current?.getBoundingClientRect()?.width ?? null;
    if (!width) return;

    const w = Math.round((width * scaleFactor) / 100) * 100;

    const mainUrl = collectImageUrl({
      id,
      options: {w, dpr: devicePixelRatio > 2 ? 2 : devicePixelRatio},
    });

    const blurUrl = collectImageUrl({
      id,
      options: {
        w: w / 2,
        dpr: 0.5,
        blur: 20,
      },
    });

    const errorUrl = collectImageUrl({
      id: IMAGES_INITIAL,
      options: {w, dpr: 1},
    });

    setPhotoUrls({mainUrl, blurUrl, errorUrl});
  }, [id, scaleFactor, boxRef]);

  const debouncedUpdate = useMemo(() => debounce(onSetPhotoUrls, 500), [onSetPhotoUrls]);

  useEffect(() => {
    if (id) {
      onSetPhotoUrls();
      didInit.current = true;
    }
  }, [id, onSetPhotoUrls]);

  useEffect(() => {
    if (didInit.current && refreshKey !== undefined) {
      debouncedUpdate();
    }
  }, [refreshKey, debouncedUpdate]);

  useEffect(() => {
    window.addEventListener("resize", debouncedUpdate);
    return () => {
      window.removeEventListener("resize", debouncedUpdate);
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  return photoUrls;
};